import type { Notebook } from "@engram/core";
import { ArrowDownUp, ArrowLeft, Book, BookCopy, BookPlus, ChevronDown, ChevronRight, FileText, MoreHorizontal, Plus, Search, X } from "lucide-react";
import { Fragment, useState } from "react";
import { useLocation } from "wouter";
import {
  addShortcut, createNote, createNotebook, createStack, deleteNotebook, defaultNotebookId, moveNotebookToStack, notebookNameError, renameNotebook, setSetting,
} from "../db/actions";
import { useQuery } from "../db/client";
import { collator, plural, relativeDate } from "./format";
import { useLayout } from "./layout";
import { Confirm, Menu, Prompt, useAnchor } from "./Menu";
import { newNoteHere } from "./newNote";
import { buildTree, useNesting } from "./notebookTree";

type Row = Notebook & { n: number; last: number | null };

export function NotebooksPage() {
  const [path, navigate] = useLocation();
  const phone = useLayout() !== "wide";
  const [searching, setSearching] = useState(false);
  const [sortBy, setSortBy] = useState<"name" | "updated">("name");
  const sortMenu = useAnchor();
  const moreMenu = useAnchor();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [dialog, setDialog] = useState<null | { kind: "new" | "newStack" } | { kind: "rename" | "delete" | "stack"; nb: Row }>(null);
  const rows = useQuery<Row>(
    // Counts and last-updated in one pass over notes (two subqueries per notebook scanned them 2 × notebooks times).
    `SELECT nb.*, COALESCE(c.n, 0) AS n, c.last AS last
     FROM notebooks nb LEFT JOIN (SELECT notebook_id, COUNT(*) AS n, MAX(updated) AS last FROM notes WHERE deleted = 0 AND trashed_at IS NULL GROUP BY notebook_id) c ON c.notebook_id = nb.id
     WHERE nb.deleted = 0 ORDER BY nb.name COLLATE NOCASE`,
    [],
    ["notebooks", "notes"],
  );
  const defaultNb = useQuery<{ value: string }>("SELECT value FROM settings WHERE id = 'defaultNotebook'", [], ["settings"])?.[0];
  const defaultId = defaultNb ? (JSON.parse(defaultNb.value) as string) : null;

  const all = rows ?? [];
  const stacks = all.filter((r) => r.kind === "stack");
  const notebooks = all.filter((r) => r.kind === "notebook");
  const tree = buildTree(all);
  const match = (r: Row) => !q || r.name.toLowerCase().includes(q.toLowerCase());
  // A stack shows when it or anything inside it matches the search; inside a matching stack everything shows.
  const shows = (r: Row): boolean => match(r) || (r.kind === "stack" && tree.children(r.id).some(shows));
  const kidsOf = (s: Row) => tree.children(s.id).filter((c) => match(s) || shows(c));
  const top = tree.top.filter(shows);

  const dialogs = (
    <>
      {dialog?.kind === "new" && <Prompt title="Create new notebook" label="Name" confirm="Create" validate={notebookNameError("notebook")} onSubmit={async (v) => navigate(`/notebook/${await createNotebook(v)}`)} onClose={() => setDialog(null)} />}
      {dialog?.kind === "newStack" && <Prompt title="Create new stack" label="Name" confirm="Create" validate={notebookNameError("stack")} onSubmit={(v) => void createStack(v)} onClose={() => setDialog(null)} />}
      {dialog?.kind === "rename" && <Prompt title={`Rename ${dialog.nb.kind}`} initial={dialog.nb.name} validate={notebookNameError(dialog.nb.kind, dialog.nb.id)} onSubmit={(v) => void renameNotebook(dialog.nb.id, v)} onClose={() => setDialog(null)} />}
      {dialog?.kind === "delete" && (
        <Confirm
          title={`Delete ${dialog.nb.kind}?`}
          body={dialog.nb.kind === "stack" ? "The notebooks in this stack are kept." : `${plural(dialog.nb.n, "note")} in "${dialog.nb.name}" will move to the Trash.`}
          confirm="Delete"
          danger
          onConfirm={() => void deleteNotebook(dialog.nb.id)}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === "stack" && <StackPrompt notebook={dialog.nb} stacks={stacks} onClose={() => setDialog(null)} />}
    </>
  );

  if (phone) {
    // Evernote Android: a full-width list (icon, name, count) under "24 NOTEBOOKS", a top bar with back,
    // search, sort and more, and a round button for a new note.
    const lastOf = (r: Row): number => (r.kind === "stack" ? Math.max(0, ...tree.notebooksUnder(r.id).map((c) => c.last ?? 0)) : (r.last ?? 0));
    const order = (list: Row[]) => (sortBy === "updated" ? [...list].sort((a, b) => lastOf(b) - lastOf(a)) : list);
    const pad = (depth: number) => (depth ? { paddingLeft: 16 + depth * 24 } : undefined);
    const row = (nb: Row, depth = 0) => (
      <li key={nb.id} className="nb-row" style={pad(depth)}>
        <button className="nb-row-main" onClick={() => navigate(`/notebook/${nb.id}`)}>
          <Book size={18} className="muted" />
          <span className="nb-row-name">{nb.name}</span>
          {nb.id === defaultId && <span className="chip small">Default</span>}
          <span className="nb-row-count">{nb.n}</span>
        </button>
        <RowMenu row={nb} stacks={stacks} onDialog={setDialog} isDefault={nb.id === defaultId} />
      </li>
    );
    const branch = (r: Row, depth: number): React.ReactNode =>
      r.kind === "stack" ? (
        <Fragment key={r.id}>
          <li className="nb-row stack" style={pad(depth)}>
            <button className="nb-row-main" onClick={() => setOpen((o) => ({ ...o, [r.id]: !(o[r.id] ?? true) }))}>
              <BookCopy size={18} className="muted" />
              <span className="nb-row-name">{r.name}</span>
              <span className="nb-row-count">{tree.notebooksUnder(r.id).length}</span>
              {(open[r.id] ?? true) ? <ChevronDown size={16} className="muted" /> : <ChevronRight size={16} className="muted" />}
            </button>
            <RowMenu row={r} stacks={stacks} onDialog={setDialog} isDefault={false} />
          </li>
          {(open[r.id] ?? true) && order(kidsOf(r)).map((c) => branch(c, depth + 1))}
        </Fragment>
      ) : (
        row(r, depth)
      );
    return (
      <div className="page phone-notebooks">
        <div className="phone-topbar">
          <button className="icon-btn" aria-label="Back" onClick={() => history.back()}>
            <ArrowLeft size={20} />
          </button>
          {searching ? (
            <input className="phone-search" autoFocus placeholder="Find notebooks…" value={q} onChange={(e) => setQ(e.target.value)} />
          ) : (
            <div className="grow" />
          )}
          <button
            className="icon-btn"
            aria-label={searching ? "Close search" : "Search notebooks"}
            onClick={() => {
              if (searching) setQ("");
              setSearching(!searching);
            }}
          >
            {searching ? <X size={20} /> : <Search size={20} />}
          </button>
          <button className="icon-btn" aria-label="Sort" onClick={sortMenu.open}>
            <ArrowDownUp size={20} />
          </button>
          <button className="icon-btn" aria-label="More" onClick={moreMenu.open}>
            <MoreHorizontal size={20} />
          </button>
        </div>
        <div className="caps-header">{plural(notebooks.length, "notebook").toUpperCase()}</div>
        <ul className="nb-list">
          {order(top).map((r) => branch(r, 0))}
        </ul>
        <button className="fab" aria-label="New note" onClick={() => void newNoteHere(path, navigate)}>
          <Plus size={26} strokeWidth={2.4} />
        </button>
        {sortMenu.anchor && (
          <Menu
            anchor={sortMenu.anchor}
            align="end"
            onClose={sortMenu.close}
            entries={[
              { label: "Name", checked: sortBy === "name", onSelect: () => setSortBy("name") },
              { label: "Date updated", checked: sortBy === "updated", onSelect: () => setSortBy("updated") },
            ]}
          />
        )}
        {moreMenu.anchor && (
          <Menu
            anchor={moreMenu.anchor}
            align="end"
            onClose={moreMenu.close}
            entries={[
              { label: "New notebook", icon: <BookPlus size={14} className="ic-create" />, onSelect: () => setDialog({ kind: "new" }) },
              { label: "New stack", icon: <BookCopy size={14} />, onSelect: () => setDialog({ kind: "newStack" }) },
            ]}
          />
        )}
        {dialogs}
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1>
          Notebooks <span className="title-count">{notebooks.length}</span>
        </h1>
      </div>
      <div className="page-toolbar">
        <div className="grow" />
        <button className="btn" onClick={() => setDialog({ kind: "newStack" })}><BookCopy size={14} /> New stack</button>
        <button className="btn" onClick={() => setDialog({ kind: "new" })}><BookPlus size={14} className="ic-create" /> New notebook</button>
        <label className="find-input">
          <Search size={14} />
          <input placeholder="Find notebooks…" value={q} onChange={(e) => setQ(e.target.value)} />
        </label>
      </div>
      <table className="table nb-table">
        <thead>
          <tr>
            <th>Title</th>
            <th className="col-date">Updated</th>
            <th className="col-actions" />
          </tr>
        </thead>
        <tbody>
          {top.map((t) => branch(t, 0))}
        </tbody>
      </table>
      {dialogs}
    </div>
  );

  function branch(r: Row, depth: number): React.ReactNode {
    return r.kind === "stack" ? (
      <Fragment key={r.id}>
        <tr className="row-stack">
          <td style={depth ? { paddingLeft: 8 + depth * 24 } : undefined}>
            <button className="row-toggle" onClick={() => setOpen((o) => ({ ...o, [r.id]: !(o[r.id] ?? true) }))}>
              {(open[r.id] ?? true) ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </button>
            <BookCopy size={15} className="muted" />
            <button className="row-title" onClick={() => navigate(`/stack/${r.id}`)}>{r.name}</button>
          </td>
          <td className="col-date muted" />
          <td className="col-actions"><RowMenu row={r} stacks={stacks} onDialog={setDialog} isDefault={false} /></td>
        </tr>
        {(open[r.id] ?? true) && kidsOf(r).map((c) => branch(c, depth + 1))}
      </Fragment>
    ) : (
      <NotebookRow key={r.id} nb={r} depth={depth} open={!!open[r.id]} onToggle={() => setOpen((o) => ({ ...o, [r.id]: !o[r.id] }))} stacks={stacks} onDialog={setDialog} isDefault={r.id === defaultId} />
    );
  }
}

/** "Add to stack": an existing stack by name, or a new one. Also moves a stack into another (nesting on). */
export function StackPrompt({ notebook, stacks, onClose }: { notebook: Notebook; stacks: Notebook[]; onClose: () => void }) {
  const tree = buildTree(stacks);
  return (
    <Prompt
      title={notebook.parent_id ? "Move to another stack" : notebook.kind === "stack" ? "Move into a stack" : "Add to stack"}
      label="Stack name (a new stack is created if it doesn't exist)"
      initial={stacks.find((s) => s.id === notebook.parent_id)?.name ?? ""}
      confirm="Move"
      validate={(v) => {
        const existing = stacks.find((s) => s.name.toLowerCase() === v.toLowerCase());
        return notebook.kind === "stack" && existing && tree.within(existing.id, notebook.id) ? "A stack can't go inside itself." : null;
      }}
      onSubmit={async (v) => {
        const existing = stacks.find((s) => s.name.toLowerCase() === v.toLowerCase());
        await moveNotebookToStack(notebook.id, existing?.id ?? (await createStack(v)));
      }}
      onClose={onClose}
    />
  );
}

type SetDialog = (d: { kind: "rename" | "delete" | "stack"; nb: Row }) => void;

function NotebookRow({ nb, depth, open, onToggle, stacks, onDialog, isDefault }: { nb: Row; depth: number; open: boolean; onToggle: () => void; stacks: Row[]; onDialog: SetDialog; isDefault: boolean }) {
  const [, navigate] = useLocation();
  const notes = useQuery<{ id: string; title: string; updated: number }>(
    open ? "SELECT id, title, updated FROM notes WHERE notebook_id = ? AND deleted = 0 AND trashed_at IS NULL ORDER BY updated DESC" : null,
    [nb.id],
    ["notes"],
  );
  return (
    <>
      <tr>
        <td style={depth ? { paddingLeft: 8 + depth * 24 } : undefined}>
          <button className="row-toggle" onClick={onToggle} disabled={!nb.n}>
            {nb.n ? open ? <ChevronDown size={14} /> : <ChevronRight size={14} /> : null}
          </button>
          <Book size={15} className="muted" />
          <button className="row-title" onClick={() => navigate(`/notebook/${nb.id}`)}>
            {nb.name} {nb.kind === "notebook" && <span className="row-count">{nb.n}</span>}
          </button>
          {isDefault && <span className="chip small">Default</span>}
        </td>
        <td className="col-date muted">{nb.last ? relativeDate(nb.last) : "—"}</td>
        <td className="col-actions"><RowMenu row={nb} stacks={stacks} onDialog={onDialog} isDefault={isDefault} /></td>
      </tr>
      {open &&
        notes?.map((n) => (
          <tr key={n.id} className="row-note">
            <td style={{ paddingLeft: 32 + depth * 24 }}>
              <FileText size={14} className="muted" />
              <button className="row-title" onClick={() => navigate(`/notebook/${nb.id}?n=${n.id}`)}>{n.title || "Untitled"}</button>
            </td>
            <td className="col-date muted">{relativeDate(n.updated)}</td>
            <td />
          </tr>
        ))}
    </>
  );
}

function RowMenu({ row, onDialog, isDefault }: { row: Row; stacks: Row[]; onDialog: SetDialog; isDefault: boolean }) {
  const m = useAnchor();
  const [, navigate] = useLocation();
  const nesting = useNesting();
  return (
    <>
      <button className="icon-btn" onClick={m.open} aria-label="Notebook actions"><MoreHorizontal size={16} /></button>
      {m.anchor && (
        <Menu
          anchor={m.anchor}
          align="end"
          onClose={m.close}
          entries={
            row.kind === "stack"
              ? [
                  { label: "Add to Shortcuts", onSelect: () => void addShortcut("stack", row.id) },
                  { label: "Rename stack", onSelect: () => onDialog({ kind: "rename", nb: row }) },
                  ...(nesting || row.parent_id
                    ? [
                        ...(nesting ? [{ label: row.parent_id ? "Move to another stack…" : "Move into a stack…", onSelect: () => onDialog({ kind: "stack", nb: row }) }] : []),
                        ...(row.parent_id ? [{ label: "Move out of stack", onSelect: () => void moveNotebookToStack(row.id, null) }] : []),
                      ]
                    : []),
                  { separator: true },
                  { label: "Delete stack", danger: true, onSelect: () => onDialog({ kind: "delete", nb: row }) },
                ]
              : [
                  // Evernote's order.
                  { label: "Add new note", onSelect: async () => navigate(`/notebook/${row.id}?n=${await createNote({ notebookId: row.id })}`) },
                  { label: "Rename notebook", onSelect: () => onDialog({ kind: "rename", nb: row }) },
                  {
                    label: "Delete notebook",
                    danger: true,
                    disabled: isDefault,
                    hint: isDefault ? "Default notebook" : undefined,
                    onSelect: async () => {
                      if ((await defaultNotebookId()) !== row.id) onDialog({ kind: "delete", nb: row });
                    },
                  },
                  { separator: true },
                  { label: "Add to Shortcuts", onSelect: () => void addShortcut("notebook", row.id) },
                  { label: "Set as default notebook", disabled: isDefault, onSelect: () => void setSetting("defaultNotebook", row.id) },
                  { label: row.parent_id ? "Move to another stack…" : "Add to stack…", onSelect: () => onDialog({ kind: "stack", nb: row }) },
                  ...(row.parent_id ? [{ label: "Remove from stack", onSelect: () => void moveNotebookToStack(row.id, null) }] : []),
                ]
          }
        />
      )}
    </>
  );
}
